/**
 * Recorded-duration lookup against the durable offline sync queue.
 *
 * The in-memory React Query cache is the fast path for "how much audio does
 * this consult already hold" — a same-process stop patches `session.duration`
 * straight onto the viewed entry. That patch dies with the process. The sync
 * queue does not: it is rehydrated from SQLite on mount, so after a quit and
 * relaunch it is the only surviving source of the recorded elapsed time.
 *
 * Both timer seeds consult it as a LAST resort, behind the live timer and the
 * cached `session.duration`, so a live or freshly-patched value always wins and
 * this only fills the cold-boot hole.
 */

/** Milliseconds as Rust records them → the seconds `session.duration` speaks. */
export function recordedSecondsFromDurationMs(
  recordedDurationMs: number | null | undefined
): number | undefined {
  // Rounded, not floored, so a value read back from the durable row and one
  // written live from the same recording agree. A null/zero/negative row value
  // lands as `undefined` rather than `0`: "never recorded" and "recorded
  // nothing" must stay indistinguishable, or a zero would satisfy a `??` and
  // shadow a real value further down the chain.
  if (typeof recordedDurationMs !== 'number' || recordedDurationMs <= 0) {
    return undefined;
  }
  return Math.round(recordedDurationMs / 1000);
}

/**
 * Recorded seconds for the consult behind `sessionId`, from the durable queue.
 *
 * Matches on EITHER id. An offline consult is keyed locally for its whole life,
 * but the detail route switches to the server id the moment one exists — so the
 * id being viewed is routinely not the id the queue row is keyed on.
 *
 * **The largest resolvable duration among matching rows wins, not the first.**
 * Two rows can carry one server id — the ordinary state once a consult is
 * created and then recorded into — and a first-match scan binds whichever Rust
 * happened to list first. `OfflineRecordingItem::from_session` builds the
 * created-but-never-recorded shell with `recorded_duration_ms: None`, so when
 * the shell is listed first a first-match answers `undefined`, the captured
 * row's real duration is never read, and the header restarts at 00:00 on the
 * relaunch-resume this lookup exists to serve.
 *
 * Largest rather than "first with a value" because it also settles the
 * multi-take case without a second rule: the longest take is the one whose
 * audio the consult holds. Rows with no resolvable duration are skipped rather
 * than treated as `0`, preserving the never-recorded / recorded-nothing
 * distinction {@link recordedSecondsFromDurationMs} is careful about.
 */
export function findQueuedRecordedSeconds(
  items: ReadonlyArray<{
    localSessionId: string;
    serverSessionId: string | null;
    // Optional, not just nullable: a row deserialised by an older bridge build
    // may omit the column entirely, and `recordedSecondsFromDurationMs` already
    // treats absent and null the same way.
    recordedDurationMs?: number | null;
  }>,
  sessionId: string | undefined
): number | undefined {
  if (!sessionId) return undefined;

  let longest: number | undefined;
  for (const item of items) {
    if (item.localSessionId !== sessionId && item.serverSessionId !== sessionId) {
      continue;
    }
    const seconds = recordedSecondsFromDurationMs(item.recordedDurationMs);
    if (seconds !== undefined && (longest === undefined || seconds > longest)) {
      longest = seconds;
    }
  }
  return longest;
}
